/**
 * Shared state for the product picker used by the order, sample service and
 * showroom visit forms: a product-type filter, a show-more cap, and a single
 * product selection.
 *
 * The markup is server-rendered by `<x-product-picker>` — this owns only the
 * state those elements bind against, so nothing here queries or mutates the
 * DOM. Composing forms spread it into their own Alpine component and may
 * implement `onProductChange(id)` to reset whatever depends on the selection.
 *
 * @param {{ products?: Array<{id: number, type: string}>, maxItems?: number }} config
 */
export function productPicker({ products = [], maxItems = 6 } = {}) {
  return {
    products,
    maxItems,
    activeFilter: '*',
    expanded: false,
    promotedProductId: null,
    selectedProductId: null,

    /**
     * A deep-linked product is promoted to the front of the grid so it stays
     * within the show-more cap. Rendering follows this order via CSS `order`.
     */
    get orderedProducts() {
      if (this.promotedProductId === null) {
        return this.products;
      }

      return [
        ...this.products.filter((product) => product.id === this.promotedProductId),
        ...this.products.filter((product) => product.id !== this.promotedProductId),
      ];
    },

    /**
     * Filtering reveals every match regardless of the cap; only the unfiltered
     * grid is capped.
     */
    get visibleProductIds() {
      const matching =
        this.activeFilter === '*'
          ? this.orderedProducts
          : this.orderedProducts.filter((product) => product.type === this.activeFilter);

      const capped =
        this.activeFilter === '*' && !this.expanded ? matching.slice(0, this.maxItems) : matching;

      return capped.map((product) => product.id);
    },

    /** The toggle is only meaningful on the full, capped grid. */
    get showToggle() {
      return this.activeFilter === '*' && this.products.length > this.maxItems;
    },

    isProductVisible(id) {
      return this.visibleProductIds.includes(id);
    },

    isProductSelected(id) {
      return this.selectedProductId === id;
    },

    isProductPromoted(id) {
      return this.promotedProductId === id;
    },

    /** Clicking the active filter clears it. */
    toggleFilter(type) {
      this.activeFilter = this.activeFilter === type ? '*' : type;

      // A selection that no longer matches the filter is given up, since the
      // user can no longer see what they picked.
      if (this.selectedProductId !== null && !this.isProductVisible(this.selectedProductId)) {
        this.selectProduct(null);
      }
    },

    toggleExpanded() {
      this.expanded = !this.expanded;
    },

    /** Passing the already-selected id clears the selection. */
    selectProduct(id) {
      const next = id !== null && this.selectedProductId === id ? null : id;

      if (next === this.selectedProductId) {
        return;
      }

      this.selectedProductId = next;
      this.onProductChange?.(next);
    },

    productType(id) {
      return this.products.find((product) => product.id === id)?.type ?? null;
    },

    /**
     * Preselect a product arriving from a `?product_id=` deep link, promoting
     * it into view and narrowing the grid to its type.
     */
    applyDeepLinkedProduct(id) {
      const type = this.productType(id);

      if (type === null) {
        return false;
      }

      this.promotedProductId = id;
      this.activeFilter = type;
      this.selectProduct(id);

      return true;
    },
  };
}

/**
 * Compose a form component on top of the picker.
 *
 * Object spread must not be used here: it *evaluates* getters and copies the
 * resulting values, which would freeze every derived property at construction
 * time — filtering and the show-more cap would silently stop updating. Merging
 * property descriptors keeps the getters lazy and reactive.
 */
export function withProductPicker(config, component) {
  const composed = Object.defineProperties(
    {},
    Object.getOwnPropertyDescriptors(productPicker(config)),
  );

  return Object.defineProperties(composed, Object.getOwnPropertyDescriptors(component));
}
